import { AxiosError } from "axios";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import BackgroundInformationApi from "../../../api/services/BackgroundInformationApi";
import TestRequestApi from "../../../api/services/TestRequestApi";
import { UserReferenceResponse } from "../../../api/services/UserInfoApi";
import CustomProgress from "../../../components/Alert/CustomProgress";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { NavigationContext } from "../../../contexts/NavigationContext";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import { UserContext } from "../../../contexts/UserContext";
import isArrayContentsEqual from "../../../functions/isArrayContentsEqual.function";
import scrollToError from "../../../functions/scrollToError.function";
import useEnv from "../../../hooks/env";
import HiddenError from "../../../models/HiddenError";
import { HttpErrorDataType } from "../../../models/HttpErrorType";
import ValidationError from "../../../models/ValidationError";
import { tesaBlue, tesaDarkRedError } from "../../../styles/colors";
import { fadeIn } from "../../../styles/springs";
import { EditRequestNameDialogProps } from "../../Dashboard/EditRequestNameDialog";
import CustomFooterGraphic from "../CustomFooterGraphic";
import CommentsSection from "./CommentsSection";
import DescriptionFormButtons from "./DescriptionFormButtons";
import DescriptionFormDialogs from "./DescriptionFormDialogs";
import getValidationSchemaSales from "./forms/DescriptionFormSales/DescriptionFormSalesValidationSchema";
import { EditRequestNameDialogHolderRef } from "./EditRequestNameDialogHolder";
import DescriptionFormType from "./models/Description.form";
import mapToCreateRequest, { mapToUpdateRequest } from "./models/Description.request-mapper";
import mapFromResponse from "./models/Description.response-mapper";
import useContextFields from "./useContextFields.hook";
import * as SalesFormFields from "./forms/fieldNames";
import { ProductSuggestionFormType } from "./models/ProductSuggestion.form-model";
import ProductEditData from "./ProductEditData.model";
import ProductReferenceResponse, {
  ProductReferenceResponseWithType,
} from "../../../api/models/ProductReferenceResponse";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import PrototypeSampleAddFormType from "../../../api/models/product/PrototypeSampleAddFormType";
import { CompetitorAddFormType } from "../../../api/models/product/CompetitorSampleAddFormType";
import useStyles from "./DescriptionTabStyles";
import CrmTestRequestApi, { TesaOpportunityProductValue } from "../../../api/services/CrmTestRequestApi";
import DescriptionFormInternal from "./forms/DescriptionFormInternal/DescriptionFormInternal";
import getValidationSchemaInternal from "./forms/DescriptionFormInternal/DescriptionFormInternalValidationSchema";
import DescriptionFormSales from "./forms/DescriptionFormSales/DescriptionFormSales";
import CSCLaboratory from "../../Admin/AdminUserOverview/CSCLaboratory";
import EnumType from "../../../models/EnumType";
import TestRequestProcessType from "../../../api/models/test-request/TestRequestProcessType";
import ProjectConfidentiality from "../../../api/models/test-request/ProjectConfidentiality";
import TestRequestPurpose from "../../../api/models/test-request/TestRequestPurpose";
import ProductApi, { ProductPropertiesResponse } from "../../../api/services/ProductApi";

type ConfirmationOverride = {
  [K in keyof DescriptionFormType]?: boolean;
};
type TestRequestDescriptionAttachment = Required<TestRequestRaw.Schemas.TestRequestAttachmentReferenceDTO>;
type TestRequestPostPayload = Required<TestRequestRaw.Schemas.CreateTestRequestRQ>;
type BusinessType = EnumType<TestRequestRaw.Schemas.BusinessType>;

// eslint-disable-next-line no-shadow
enum TesaRequestType {
  INTERNAL = "604620000", // Application Engineering (AE/ASE)
  APROE = "604620001", //  Process Engineering (AProE)
}

const initialFormValues: DescriptionFormType = {
  applicationField: "",
  testRequestPriority: "NONE",
  assignee: null,
  backgroundInformation: "",
  businessType: "" as EnumType<BusinessType>,
  businessUnit: "",
  competitorProductReferences: [],
  crmProjectNo: "",
  cscLaboratory: "" as EnumType<CSCLaboratory>,
  currentSolution: "",
  customerClassification: "",
  customerPartList: "",
  deadline: "",
  debitorNumber: "",
  directCustomer: null,
  directCustomerCGR: "",
  directCustomerLocation: null,
  distributor: null,
  estimatedFinishingDate: "",
  estimatedTestEffortInDays: "",
  externalDevelopmentBudget: "",
  externalDevelopmentBudgetCurrencyCode: "_UNDEFINED" as EnumType<TestRequestRaw.Schemas.CurrencyCode>,
  furtherInternalContact: "",
  furtherInternalContacts: [],
  marketSegment: "",
  oem: null,
  opportunityProducts: [],
  pnsPotential: "",
  pnsPotentialCurrencyCode: "",
  processDetail: "",
  processType: "NEW" as EnumType<TestRequestProcessType>,
  productReferences: [],
  products: "",
  projectConfidentiality: "" as EnumType<Exclude<ProjectConfidentiality, "CONFIDENTIAL" | "NDA" | "NONE">>,
  prototypeProductReferences: [],
  reportLanguage: "" as EnumType<TestRequestRaw.Schemas.ReportLanguage>,
  reportUsage: "",
  salesTeam: [],
  specificationNeeded: false,
  specifications: [],
  substrates: "",
  suggestedApprover: null,
  testLocation: "CSC_LAB" as EnumType<TestRequestRaw.Schemas.TestLocation>,
  testProgram: "",
  testPurpose: "",
  title: "",
};

interface CrmFieldValues {
  title: string;
  oem: { name: string };
  cgr: string;
  crmProjectNo: string;
  businessUnit: string;
  marketSegment: string;
  applicationField: string;
  businessType: BusinessType;
  customerName?: { id: number; name: string };
  directCustomerLocation?: { id: number; name: string };
  debitorNumber: string;
  distributorName: { name: string; country: string; id: string } | null;
  customerClassification: string;
  pnsPotential: string;
  pnsPotentialCurrencyCode: string;
  projectConfidentiality: "INTERNAL" | "HIGHLY_CONFIDENTIAL";
  productReferences: ProductReferenceResponse[];
  opportunityProducts: ProductReferenceResponse[];
  reportUsage: EnumType<TestRequestRaw.Schemas.ReportUsage>;
  testPurpose: EnumType<TestRequestRaw.Schemas.TestRequestTestPurpose>;
  testProgram: string;
}

function isHighConfidentialityRequired(error: AxiosError<HttpErrorDataType>): boolean {
  const errorMessage = "Restriction on confidentiality was not met.";
  return (
    Boolean(error.response?.data.propertyViolations?.some((pv) => pv.message === errorMessage)) ||
    Boolean(error.response?.data.classViolations?.some((pv) => pv.message === errorMessage))
  );
}

function mustConfirmContacts(newValues: DescriptionFormType, initialValues: DescriptionFormType) {
  return (
    newValues.projectConfidentiality === "HIGHLY_CONFIDENTIAL" &&
    newValues.furtherInternalContacts.length &&
    !isArrayContentsEqual<UserReferenceResponse>(
      initialValues.furtherInternalContacts,
      newValues.furtherInternalContacts,
      "userId",
    )
  );
}

interface DescriptionTabProps {
  initialValuesFromUrl?: DescriptionFormType;
}

const DescriptionTab = (props: DescriptionTabProps) => {
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const springPropsFadeIn = useSpring(fadeIn);

  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const testRequestApi = new TestRequestApi(api);
  const backgroundInformationApi = new BackgroundInformationApi(api);

  const history = useHistory();
  const formRef = useRef<FormikProps<DescriptionFormType> | null>(null);

  const { user } = useContext(UserContext);
  const dialogContext = useContext(ConfirmationDialogContext);
  const { showConfirmationDialog } = dialogContext;
  const permissions = useContext(TestRequestPermissionContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const {
    testRequestTitleSuffix,
    updateTestRequestTitleSuffix,
    updateTestRequestObj,
    testRequestObj,
    testRequestHistorical,
  } = useContext(TestRequestContext);
  const {
    dirtyFlag,
    updateDirtyFlag,
    updatePreventNav,
    preventNav: navigationContextPreventNav,
  } = useContext(NavigationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isInternalContactDialogOpen, setIsInternalContactDialogOpen] = useState(false);
  const [isSalesProductsDialogOpen, setIsSalesProductsDialogOpen] = useState(false);
  const [isAddPrototypeDialogOpen, setIsAddPrototypeDialogOpen] = useState<boolean>(false);
  const [isCompetitorDialogOpen, setIsCompetitorDialogOpen] = useState<boolean>(false);
  const [attachedFilesReferences, setAttachedFilesReferences] = useState<TestRequestDescriptionAttachment[]>(
    testRequestObj?.descriptionAttachments || [],
  );
  const [selectedSalesProduct, setSelectedSalesProduct] = useState<ProductEditData<ProductReferenceResponse> | null>(
    null,
  );

  const queryParams = new URLSearchParams(window.location.search);
  const opportunityId = queryParams.get("opportunityId") || "";
  const requestType = queryParams.get("requestType") || "";
  const historical = queryParams.get("historical") || "0";

  const editRequestNameDialogRef = useRef<EditRequestNameDialogHolderRef | null>(null);
  // Indicates "new" requests
  const createMode = testRequestId === "new";
  // Used to save without validation for DRAFT requests
  const newOrDraft = createMode || testRequestObj?.state === "DRAFT";
  const crmTestRequestApi = new CrmTestRequestApi(api);
  const [initialValues, setInitialValues] = useState<DescriptionFormType>(initialFormValues);
  const [crmFieldValues, setCrmFieldValues] = useState<CrmFieldValues | null>(null);

  const getBusinessType = (revenueType: string | null): BusinessType => {
    if (!revenueType) {
      return "";
    }
    switch (revenueType.toLowerCase()) {
      case "incremental":
        return "NEW";
      case "replacement":
        return "MAINTAIN";
      default:
        return "";
    }
  };

  const extractInnermostPText = (htmlString?: string): string => {
    if (!htmlString) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const pTags = doc.getElementsByTagName("p");
    if (pTags.length === 0) return "";
    const innermostP = pTags[pTags.length - 1];
    return innermostP.innerText || "";
  };

  const getProductReferencesByNarts = async (productRefsList: TesaOpportunityProductValue[]) => {
    const sapProductRefs: ProductPropertiesResponse[] = [];
    const sapOpportunityListProductRefs: ProductReferenceResponse[] = [];
    const promises = productRefsList.map(async ({ tesa_ProductId }) => {
      if (tesa_ProductId && tesa_ProductId.name) {
        try {
          const { data } = await productApi.getProductWithPropsByNart(tesa_ProductId.name);
          const { nart, baseProduct, productName } = data;

          // Necessary during testing to avoid null values from SAP
          if (!baseProduct || !productName || !nart) {
            return;
          }

          sapProductRefs.push({ nart, baseProduct, productName });
          sapOpportunityListProductRefs.push({ name: productName });
        } catch (error) {
          handleHttpError(error as AxiosError<HttpErrorDataType>);
        }
      }
    });

    await Promise.all(promises);

    return { sapProductRefs, sapOpportunityListProductRefs };
  };

  const fetchCrmTestRequest = async (reqType?: string, oppId?: string): Promise<void> => {
    if (!reqType || !oppId) {
      return;
    }
    setIsLoading(true);
    try {
      const opportunityReference = await crmTestRequestApi.getCrmOpportunityById(oppId);
      const opportunityProductReference = await crmTestRequestApi.getCrmOpportunityProductById(oppId);
      const customerReference = await crmTestRequestApi.getCrmCustomerReferenceByOpportunityId(oppId);

      // Use NARTs to get product references from SAP
      const productRefs = await getProductReferencesByNarts(opportunityProductReference.data);

      const testPurpose: EnumType<TestRequestRaw.Schemas.TestRequestTestPurpose> = opportunityId ? "CUSTOMER_TEST" : "";
      const reportUsage: EnumType<TestRequestRaw.Schemas.ReportUsage> = opportunityId ? "EXTERNAL" : "ONLY_INTERNAL";
      const opportunityData = opportunityReference.data;
      const distributor = opportunityData?.tesa_DistributorId;
      const applicationId = opportunityData?.tesa_ApplicationId;
      const oemId = opportunityData?.tesa_OEMId;
      const cgrId = opportunityData?.tesa_CGRId;
      const parentAccountId = opportunityData?.parentaccountid;
      const transactionCurrencyId = opportunityData?.transactioncurrencyid;
      const testProgram = extractInnermostPText(opportunityData?.description);

      setCrmFieldValues({
        title: opportunityData?.tse_customtitle || "",
        testPurpose,
        testProgram,
        oem: { name: oemId?.tesa_name || "" },
        cgr: cgrId?.tesa_name || "",
        crmProjectNo: opportunityData?.tesa_syncid?.slice(-6) || "",
        businessUnit: applicationId?.tse_businessunit || "",
        marketSegment: applicationId?.tse_module || "",
        applicationField: applicationId?.tesa_name || "",
        businessType: getBusinessType(
          opportunityData?.["tesa_revenuetypecode@OData.Community.Display.V1.FormattedValue"] || "",
        ),
        customerName:
          customerReference && customerReference.data
            ? { id: customerReference.data.id!, name: customerReference.data.name! }
            : undefined,
        directCustomerLocation:
          customerReference && customerReference.data && customerReference.data.country
            ? {
                id: customerReference.data.country.id!,
                name: customerReference.data.country.name!,
              }
            : undefined,
        pnsPotentialCurrencyCode: transactionCurrencyId?.isocurrencycode || "",
        customerClassification: customerReference?.data?.crmAccountClassificationCode ?? "",
        pnsPotential: String(opportunityData?.estimatedvalue || ""),
        projectConfidentiality: opportunityData?.tesa_secretproject === true ? "HIGHLY_CONFIDENTIAL" : "INTERNAL",
        distributorName: distributor
          ? {
              name: distributor.name || "",
              id: distributor.accountid || "",
              country: distributor["_tesa_address1_countryid_value@OData.Community.Display.V1.FormattedValue"] || "",
            }
          : null,
        debitorNumber: parentAccountId?.accountnumber?.toString() || "",
        productReferences: productRefs.sapProductRefs,
        opportunityProducts: productRefs.sapOpportunityListProductRefs,
        reportUsage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (createMode) {
      fetchCrmTestRequest(requestType, opportunityId)
        .then(() => {
          console.log("API fetching complete, now setting initial values");
        })
        .catch((e) => {
          if (e.response?.status === 404) {
            showSnackbar("CRM opportunity id not found", "error");
            setTimeout(() => {
              history.push("/requests");
            }, 2000);
          } else if (e.response?.status === 403) {
            showSnackbar(e.response?.data.message, "error");
            setTimeout(() => {
              history.push("/requests");
            }, 2000);
          } else {
            handleHttpError(e);
          }
        });
    }
  }, [requestType, opportunityId]);

  useEffect(() => {
    if (createMode && crmFieldValues) {
      updateTestRequestTitleSuffix(crmFieldValues.title);
      setInitialValues(
        mapFromResponse({
          ...testRequestObj.description,
          title: crmFieldValues.title,
          testProgram: crmFieldValues.testProgram,
          testPurpose: crmFieldValues.testPurpose,
          oem: crmFieldValues.oem,
          crmProjectNo: crmFieldValues.crmProjectNo,
          businessUnit: crmFieldValues.businessUnit,
          marketSegment: crmFieldValues.marketSegment,
          applicationField: crmFieldValues.applicationField,
          businessType: crmFieldValues.businessType,
          directCustomer: crmFieldValues.customerName,
          directCustomerLocation: crmFieldValues.directCustomerLocation,
          directCustomerCGR: crmFieldValues.cgr,
          pnsPotential: crmFieldValues.pnsPotential,
          pnsPotentialCurrencyCode: crmFieldValues.pnsPotentialCurrencyCode,
          customerClassification: crmFieldValues.customerClassification,
          projectConfidentiality: crmFieldValues.projectConfidentiality,
          distributor: crmFieldValues.distributorName,
          debitorNumber: crmFieldValues.debitorNumber,
          productReferences: crmFieldValues.productReferences,
          opportunityProducts: crmFieldValues.opportunityProducts,
        }),
      );
    } else if (!createMode) {
      setInitialValues(mapFromResponse(testRequestObj?.description));
    }
  }, [crmFieldValues, createMode]);

  const { preparing } = useContextFields(initialValues);

  const classes = useStyles();

  const handleCreateReq = (apiPayload: TestRequestPostPayload) => {
    apiPayload.description.crmOpportunityId = opportunityId;
    apiPayload.description.tesaRequestType = +requestType;

    if (requestType === TesaRequestType.APROE) {
      apiPayload.purpose = "APPLICATION_PROCESS";
    }
    return backgroundInformationApi
      .createBackgroundInformation(apiPayload)
      .then((res) => {
        const newReqData = res.data;

        history.push({
          pathname: `/requests/${newReqData.requestId}`,
          search: queryParams.toString(),
        });

        updateTestRequestObj(newReqData);
        showSnackbar("Successfully created.", "success");

        return Promise.resolve(newReqData);
      })
      .catch((e) => {
        handleHttpError(e);
        return Promise.reject();
      });
  };

  const handleUpdateReq = (apiPayload: TestRequestPostPayload) => {
    apiPayload.description.crmOpportunityId = opportunityId;
    apiPayload.description.tesaRequestType = +requestType;
    return backgroundInformationApi
      .updateBackgroundInformation(testRequestId, apiPayload.description)
      .then((res) => {
        const updatedReqData = res.data;

        history.push({
          pathname: `/requests/${updatedReqData.requestId}`,
          search: queryParams.toString(),
        });

        updateTestRequestObj(updatedReqData);
        showSnackbar("Successfully saved.", "success");

        return updatedReqData;
      })
      .catch((e) => {
        handleHttpError(e);
        return Promise.reject(e);
      });
  };

  const refreshDescriptionTab = (testReqData: TestRequestResponse, redirect?: boolean): Promise<void> => {
    updateTestRequestObj(testReqData);
    updateTestRequestTitleSuffix(testReqData.description?.title || "");
    updateDirtyFlag(false);
    updatePreventNav(null);
    setAttachedFilesReferences((testReqData.descriptionAttachments as TestRequestDescriptionAttachment[]) || []);

    const requestsPage = document.getElementById("requestsPage");
    requestsPage?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });

    return permissions
      .updatePermissionState()
      .then(() => {
        if (redirect) {
          history.push({
            pathname: `/requests/${testReqData.requestId}`,
            search: queryParams.toString(),
          });
        }
      })
      .catch(handleHttpError)
      .finally(() => {
        window.location.reload();
      });
  };

  const resetSalesProductInitialData = () => {
    setSelectedSalesProduct(null);
  };

  const handleSave = (
    values: DescriptionFormType,
    refresh: boolean = true,
    confirmationOverride?: ConfirmationOverride,
  ): Promise<TestRequestResponse | void> => {
    const titleForSave = testRequestTitleSuffix || values.title;
    const apiPayload = mapToCreateRequest(
      titleForSave,
      values,
      testRequestHistorical,
      testRequestObj.purpose || "DEFAULT",
    );
    if (mustConfirmContacts(values, initialValues) && !confirmationOverride?.furtherInternalContacts) {
      setIsInternalContactDialogOpen(true);
      return Promise.reject(new HiddenError("Must confirm contacts."));
    }

    if (createMode) {
      return handleCreateReq(apiPayload).then((newReqData) => {
        if (refresh) {
          return refreshDescriptionTab(newReqData, true).then(() => newReqData);
        }
        return newReqData;
      });
    }
    return handleUpdateReq(apiPayload).then((newReqData) => {
      if (refresh) {
        return refreshDescriptionTab(newReqData).then(() => newReqData);
      }
      return newReqData;
    });
  };

  const validateForm = (ensureHighlyConfidential?: boolean) => {
    if (formRef.current) {
      const form = formRef.current;

      if (ensureHighlyConfidential) {
        form.values.projectConfidentiality = "HIGHLY_CONFIDENTIAL";
      }

      return form.validateForm().then((errors) => {
        const errorKeys = Object.keys(errors) as Array<keyof DescriptionFormType>;
        if (errorKeys.length > 0) {
          form.handleSubmit();
          scrollToError();
          const errorMessage = errorKeys.length === 1 ? errors[errorKeys[0]] : undefined;
          throw new ValidationError(errors, typeof errorMessage !== "string" ? undefined : errorMessage);
        }
      });
    }
    return Promise.reject();
  };

  const preSave = (values: DescriptionFormType) => () => validateForm().then(() => handleSave(values, false));

  const handleServerValidationAndSave = (values: DescriptionFormType, actions: FormikHelpers<DescriptionFormType>) => {
    setTimeout(() => {
      actions.setSubmitting(false);
    }, 500);
    const apiPayload = mapToUpdateRequest(testRequestTitleSuffix, values);
    return testRequestApi
      .validateTestRequestDescription({ ...apiPayload, crmOpportunityId: opportunityId, tesaRequestType: +requestType })
      .then(() => {
        return handleSave(values);
      })
      .catch((e: AxiosError<HttpErrorDataType>) => {
        if (e.response?.status !== 400) {
          handleHttpError(e);
        } else if (isHighConfidentialityRequired(e)) {
          return showConfirmationDialog({
            text: <>Please make sure you selected the correct confidentiality level.</>,
            confirmText: "Set to secret project and save",
            confirmColor: tesaBlue,
          })
            .then(() => {
              actions.setFieldValue("projectConfidentiality", "HIGHLY_CONFIDENTIAL");
              return handleSave({
                ...values,
                projectConfidentiality: "HIGHLY_CONFIDENTIAL",
              });
            })
            .catch(() => {
              // do nothing on dialog close
            });
        } else {
          return handleSave(values);
        }
      });
  };

  const submitFormOnNav = (): Promise<void> => {
    const { values, setFieldValue } = formRef.current || {};
    if (!values || !setFieldValue) {
      return Promise.reject();
    }
    if (newOrDraft) {
      return handleSave(values)
        .then(() => undefined)
        .catch((error) => {
          scrollToError();
          return Promise.reject(error);
        });
    }

    return validateForm().then(() => {
      const apiPayload = mapToUpdateRequest(testRequestTitleSuffix, values);
      return testRequestApi
        .validateTestRequestDescription({
          ...apiPayload,
          crmOpportunityId: opportunityId,
          tesaRequestType: +requestType,
        })
        .then(() => {
          return handleSave(values).then(() => {});
        })
        .catch((e: AxiosError<HttpErrorDataType> | HiddenError) => {
          if (e instanceof HiddenError) {
            return Promise.reject(e);
          }
          if (e.response?.status !== 400 || !isHighConfidentialityRequired(e)) {
            handleHttpError(e);
          } else {
            return showConfirmationDialog({
              text: <>Please make sure you selected the correct confidentiality level.</>,
              confirmText: "Set to secret project and save",
              confirmColor: tesaBlue,
            }).then(() => {
              setFieldValue("projectConfidentiality", "HIGHLY_CONFIDENTIAL");
              return handleSave({
                ...values,
                projectConfidentiality: "HIGHLY_CONFIDENTIAL",
              }).then(() => {});
            });
          }
        });
    });
  };

  const preventNav = (): Promise<void> => {
    return showConfirmationDialog({
      headline: "You have unsaved changes",
      text: "If you continue, your current changes to the request description will be discarded.",
      confirmText: "Save",
      additionalAction: {
        id: "DISCARD",
        text: "Discard",
        textColor: tesaDarkRedError,
      },
    }).then((id: string) => {
      if (id === "DEFAULT") {
        updateDirtyFlag(true);
        return submitFormOnNav();
      }
      updateDirtyFlag(false);
    });
  };

  const showEditRequestNameDialog = (
    doShow: boolean,
    dialogProps?: EditRequestNameDialogProps,
  ): Promise<string | void> => {
    if (editRequestNameDialogRef.current?.isOpen()) {
      editRequestNameDialogRef.current?.cancel();
    }
    if (!doShow) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      editRequestNameDialogRef.current?.show(
        dialogProps || {
          open: true,
          onClose: () => {
            reject(new HiddenError("Dialog closed"));
            editRequestNameDialogRef.current?.hide();
          },
          onSave: (newName: string) => {
            resolve(newName.trim());
            editRequestNameDialogRef.current?.hide();
          },
        },
      );
    });
  };

  const handleDirtyChange = (resetFlag?: boolean) => {
    if (resetFlag) {
      updateDirtyFlag(false);
      updatePreventNav(null);
    } else if (!dirtyFlag) {
      updateDirtyFlag(true);
      updatePreventNav(preventNav);
    }
  };

  const createProduct = ({ nart, productName, baseProduct }: ProductSuggestionFormType): ProductReferenceResponse => ({
    nart,
    productName,
    baseProduct,
  });

  const handleAddSalesProduct = (productData: ProductSuggestionFormType) => {
    const productAdded = formRef.current?.values && [
      ...(formRef.current.values[SalesFormFields.PRODUCT_REFERENCES] || []),
      createProduct(productData),
    ];
    formRef.current?.setFieldValue(SalesFormFields.PRODUCT_REFERENCES, productAdded);
    handleDirtyChange();
    setIsSalesProductsDialogOpen(false);
  };

  const handleEditSalesProduct = (index?: number) => (productData: ProductSuggestionFormType) => {
    if (productData && index !== undefined) {
      const productsEdited =
        (formRef.current?.values && [...(formRef.current.values[SalesFormFields.PRODUCT_REFERENCES] || [])]) || [];
      productsEdited[index] = createProduct(productData);
      formRef.current?.setFieldValue(SalesFormFields.PRODUCT_REFERENCES, productsEdited);
      handleDirtyChange();
    }
    setIsSalesProductsDialogOpen(false);
  };

  const handleEditPrototypeProduct =
    (selectedProduct: ProductEditData<ProductReferenceResponseWithType> | null) =>
    (productData: PrototypeSampleAddFormType) => {
      if (productData && selectedProduct !== undefined) {
        const currentValues = formRef?.current?.values[SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES] || [];
        const removePreviousSelected = currentValues.filter((product) => {
          return (
            selectedProduct?.data?.prototypeProductId !== product.prototypeProductId ||
            selectedProduct?.data?.prototypeProductName !== product.prototypeProductName
          );
        });
        if (
          !removePreviousSelected.some(
            (o) =>
              o.prototypeProductId === productData?.prototype?.id &&
              o.prototypeProductName === productData?.prototype?.name,
          )
        ) {
          const prototypeProductToAdd = [
            ...removePreviousSelected,
            {
              prototypeProductId: productData?.prototype?.id,
              prototypeProductName: productData?.prototype?.name,
              prototypeProductResponsibleLaboratoryId: productData?.prototype?.responsibleLaboratory?.id,
              prototypeProductResponsibleLaboratoryName: productData?.prototype?.responsibleLaboratory?.name,
              type: SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES,
            },
          ];
          formRef.current?.setFieldValue(SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES, prototypeProductToAdd);
        }
      }
      handleDirtyChange();
      setIsAddPrototypeDialogOpen(false);
    };

  const handleAddPrototypeProduct = (productData: PrototypeSampleAddFormType) => {
    const currentValues = formRef?.current?.values[SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES] || [];
    if (
      !currentValues.some(
        (o) =>
          o.prototypeProductId === productData?.prototype?.id &&
          o.prototypeProductName === productData?.prototype?.name,
      )
    ) {
      const prototypeProductToAdd = [
        ...currentValues,
        {
          prototypeProductId: productData?.prototype?.id,
          prototypeProductName: productData?.prototype?.name,
          prototypeProductResponsibleLaboratoryId: productData?.prototype?.responsibleLaboratory?.id,
          prototypeProductResponsibleLaboratoryName: productData?.prototype?.responsibleLaboratory?.name,
          type: SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES,
        },
      ];
      formRef.current?.setFieldValue(SalesFormFields.PROTOTYPE_PRODUCT_REFERENCES, prototypeProductToAdd);
      handleDirtyChange();
    }
    setIsAddPrototypeDialogOpen(false);
  };

  const handleEditCompetitorProduct =
    (selectedProduct: ProductEditData<ProductReferenceResponseWithType> | null) =>
    (productData: CompetitorAddFormType) => {
      if (productData && selectedProduct !== undefined) {
        const currentValues = formRef?.current?.values[SalesFormFields.COMPETITOR_PRODUCT_REFERENCES] || [];

        const removePreviousSelected = currentValues.filter((product) => {
          return (
            selectedProduct?.data?.competitorProductId !== product.competitorProductId ||
            selectedProduct?.data?.competitorProductName !== product.competitorProductName
          );
        });
        if (
          !removePreviousSelected.some(
            (o) =>
              o.competitorProductId === productData?.product?.id &&
              o.competitorProductName === productData?.product?.name,
          )
        ) {
          const competitorProductToAdd = [
            ...removePreviousSelected,
            {
              competitorProductId: productData?.product?.id,
              competitorProductName: productData?.product?.name,
              competitorCompanyId: productData?.product?.company?.id,
              competitorCompanyName: productData?.product?.company?.name,
              type: SalesFormFields.COMPETITOR_PRODUCT_REFERENCES,
            },
          ];
          formRef.current?.setFieldValue(SalesFormFields.COMPETITOR_PRODUCT_REFERENCES, competitorProductToAdd);
        }
      }
      handleDirtyChange();
      setIsCompetitorDialogOpen(false);
    };

  const handleAddCompetitorProduct = (productData: CompetitorAddFormType) => {
    const currentValues = formRef?.current?.values[SalesFormFields.COMPETITOR_PRODUCT_REFERENCES] || [];
    if (
      !currentValues.some(
        (o) =>
          o.competitorProductId === productData?.product?.id && o.competitorProductName === productData?.product?.name,
      )
    ) {
      const competitorProductToAdd = [
        ...currentValues,
        {
          competitorProductId: productData?.product?.id,
          competitorProductName: productData?.product?.name,
          competitorCompanyId: productData?.product?.company?.id,
          competitorCompanyName: productData?.product?.company?.name,
          type: SalesFormFields.COMPETITOR_PRODUCT_REFERENCES,
        },
      ];
      formRef.current?.setFieldValue(SalesFormFields.COMPETITOR_PRODUCT_REFERENCES, competitorProductToAdd);
      handleDirtyChange();
    }
    setIsCompetitorDialogOpen(false);
  };

  const showSalesProductsDialog = (productEditData?: ProductEditData<ProductReferenceResponse>) => {
    setIsSalesProductsDialogOpen(true);
    if (productEditData) {
      setSelectedSalesProduct(productEditData);
    }
  };

  const showAddPrototypeDialog = (productEditData?: ProductEditData<ProductReferenceResponse>) => {
    setIsAddPrototypeDialogOpen(true);
    if (productEditData) {
      setSelectedSalesProduct(productEditData);
    }
  };

  const showCompetitorDialog = (productEditData?: ProductEditData<ProductReferenceResponse>) => {
    setIsCompetitorDialogOpen(true);
    if (productEditData) {
      setSelectedSalesProduct(productEditData);
    }
  };

  useEffect(() => {
    updatePreventNav(navigationContextPreventNav && preventNav);
  }, [testRequestTitleSuffix]);

  useEffect(() => {
    if (initialValues.title !== testRequestTitleSuffix) {
      setInitialValues((prevValues) => ({
        ...prevValues,
        title: testRequestTitleSuffix,
      }));
    }

    if (formRef.current?.values.title !== testRequestTitleSuffix) {
      formRef.current?.setFieldValue("title", testRequestTitleSuffix, false);
    }
  }, [initialValues.title, testRequestTitleSuffix]);

  if (isLoading || preparing || (!createMode && !testRequestObj)) {
    return <CustomProgress />;
  }

  if (!permissions.accessDescription) {
    return (
      <animated.div className={classes.root} style={springPropsFadeIn}>
        Access denied.
      </animated.div>
    );
  }
  const submissionOrApprovalDate = testRequestObj?.submissionOrApprovalDate
    ? new Date(testRequestObj?.submissionOrApprovalDate)
    : null;

  const getCurrentValidationSchema = (
    testRequestStatus: string,
    purpose: TestRequestPurpose,
    linkedWithOppId: boolean,
  ) => {
    if (linkedWithOppId) {
      return getValidationSchemaSales(testRequestStatus, purpose, linkedWithOppId);
    }
    return getValidationSchemaInternal(testRequestStatus, purpose);
  };

  const descriptionFormTemplate = (
    formikProps: FormikProps<DescriptionFormType>,
    oppId: string,
    requestType: string,
  ) => {
    if (oppId && requestType) {
      return (
        <DescriptionFormSales
          attachedFilesReferences={attachedFilesReferences}
          formikProps={formikProps}
          handleDirtyChange={handleDirtyChange}
          handleSave={handleSave}
          permissions={permissions.description}
          scrollToError={scrollToError}
          showSalesProductsDialog={showSalesProductsDialog}
          showAddPrototypeDialog={showAddPrototypeDialog}
          showCompetitorDialog={showCompetitorDialog}
          showConfirmationDialog={showConfirmationDialog}
          submissionOrApprovalDate={submissionOrApprovalDate}
          testRequestHistorical={testRequestHistorical}
          testRequestId={testRequestId}
          testRequestObj={testRequestObj}
          user={user}
        />
      );
    }
    if (!oppId && historical === "1") {
      return (
        <DescriptionFormInternal
          attachedFilesReferences={attachedFilesReferences}
          formikProps={formikProps}
          handleDirtyChange={handleDirtyChange}
          handleSave={handleSave}
          permissions={permissions.description}
          scrollToError={scrollToError}
          showConfirmationDialog={showConfirmationDialog}
          showSalesProductsDialog={showSalesProductsDialog}
          showAddPrototypeDialog={showAddPrototypeDialog}
          showCompetitorDialog={showCompetitorDialog}
          submissionOrApprovalDate={submissionOrApprovalDate}
          testRequestHistorical={testRequestHistorical}
          testRequestId={testRequestId}
          testRequestObj={testRequestObj}
          user={user}
        />
      );
    }
    if (!oppId && !requestType && testRequestObj?.type === "SALES") {
      return (
        <DescriptionFormSales
          attachedFilesReferences={attachedFilesReferences}
          formikProps={formikProps}
          handleDirtyChange={handleDirtyChange}
          handleSave={handleSave}
          permissions={permissions.description}
          scrollToError={scrollToError}
          showSalesProductsDialog={showSalesProductsDialog}
          showAddPrototypeDialog={showAddPrototypeDialog}
          showCompetitorDialog={showCompetitorDialog}
          showConfirmationDialog={showConfirmationDialog}
          submissionOrApprovalDate={submissionOrApprovalDate}
          testRequestHistorical={testRequestHistorical}
          testRequestId={testRequestId}
          testRequestObj={testRequestObj}
          user={user}
        />
      );
    }
    return (
      <DescriptionFormInternal
        attachedFilesReferences={attachedFilesReferences}
        formikProps={formikProps}
        handleDirtyChange={handleDirtyChange}
        handleSave={handleSave}
        permissions={permissions.description}
        scrollToError={scrollToError}
        showConfirmationDialog={showConfirmationDialog}
        showSalesProductsDialog={showSalesProductsDialog}
        showAddPrototypeDialog={showAddPrototypeDialog}
        showCompetitorDialog={showCompetitorDialog}
        submissionOrApprovalDate={submissionOrApprovalDate}
        testRequestHistorical={testRequestHistorical}
        testRequestId={testRequestId}
        testRequestObj={testRequestObj}
        user={user}
      />
    );
  };

  if (isLoading) {
    return <CustomProgress />;
  }

  if (!permissions.accessDescription) {
    return (
      <animated.div className={classes.root} style={springPropsFadeIn}>
        Access denied.
      </animated.div>
    );
  }

  const requestLinkedWithCrmOpportunity = Boolean(opportunityId || testRequestObj?.type === "SALES");

  return (
    <animated.div style={springPropsFadeIn}>
      <Formik
        innerRef={formRef}
        enableReinitialize={!dirtyFlag}
        initialValues={initialValues}
        onSubmit={handleServerValidationAndSave}
        validationSchema={getCurrentValidationSchema(
          (testRequestObj?.status?.state || "DRAFT") as string,
          testRequestObj.purpose,
          requestLinkedWithCrmOpportunity,
        )}
      >
        {(formikProps) => {
          return (
            <>
              {descriptionFormTemplate(formikProps, opportunityId, requestType)}
              <CommentsSection id="descriptionCommentSection" handleDirtyChange={handleDirtyChange} />
              <DescriptionFormButtons
                createMode={createMode}
                formikProps={formikProps}
                handleDirtyChange={handleDirtyChange}
                handleHttpError={handleHttpError}
                handleSave={handleSave}
                isHighConfidentialityRequired={isHighConfidentialityRequired}
                newOrDraft={newOrDraft}
                permissions={permissions}
                preSave={preSave}
                refreshDescriptionTab={refreshDescriptionTab}
                scrollToError={scrollToError}
                setIsAssignDialogOpen={setIsAssignDialogOpen}
                showEditRequestNameDialog={showEditRequestNameDialog}
                showSnackbar={showSnackbar}
                testRequestId={testRequestId}
                testRequestObj={testRequestObj}
                testRequestTitleSuffix={testRequestTitleSuffix}
                updateTestRequestTitleSuffix={updateTestRequestTitleSuffix}
                validateForm={validateForm}
              />
              <DescriptionFormDialogs
                formValues={formikProps.values}
                handleSave={handleSave}
                handleAddSalesProduct={handleAddSalesProduct}
                handleEditSalesProduct={handleEditSalesProduct(selectedSalesProduct?.index)}
                handleAddPrototypeProduct={handleAddPrototypeProduct}
                handleEditPrototypeProduct={handleEditPrototypeProduct(selectedSalesProduct)}
                handleAddCompetitorProduct={handleAddCompetitorProduct}
                handleEditCompetitorProduct={handleEditCompetitorProduct(selectedSalesProduct)}
                isAssignDialogOpen={isAssignDialogOpen}
                isInternalContactDialogOpen={isInternalContactDialogOpen}
                isSalesProductsDialogOpen={isSalesProductsDialogOpen}
                isAddPrototypeDialogOpen={isAddPrototypeDialogOpen}
                isCompetitorDialogOpen={isCompetitorDialogOpen}
                permissions={permissions.description}
                preSave={preSave(formikProps.values)}
                ref={editRequestNameDialogRef}
                refreshDescriptionTab={refreshDescriptionTab}
                resetSalesProductInitialData={resetSalesProductInitialData}
                selectedSalesProduct={selectedSalesProduct && selectedSalesProduct.data}
                setIsAssignDialogOpen={setIsAssignDialogOpen}
                setIsInternalContactDialogOpen={setIsInternalContactDialogOpen}
                setIsSalesProductsDialogOpen={setIsSalesProductsDialogOpen}
                setIsAddPrototypeDialogOpen={setIsAddPrototypeDialogOpen}
                setIsCompetitorDialogOpen={setIsCompetitorDialogOpen}
                testRequestId={testRequestId}
                testRequestPurpose={testRequestObj.purpose}
                testRequestState={testRequestObj?.status?.state}
              />
            </>
          );
        }}
      </Formik>
      <CustomFooterGraphic />
    </animated.div>
  );
};

export default DescriptionTab;
